import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useCurrentTeamName } from 'v4/entities/common/hooks/useTeam';
import { Time, getLocalTimeZone, today } from '@internationalized/date';
import { Form } from '../../design-system/Form';
import { DialogBody } from '../../design-system/Dialog';
import { FormDialogContent } from 'v5/patterns/forms/dialogs/FormDialogContent';
import { DialogErrorFooter } from 'v5/patterns/forms/dialogs/DialogErrorFooter';
import { useRegisterDirtyState } from 'v5/patterns/forms/unsaved-changes/useRegisterDirtyState';
import type { DuplicateCallsheetFormData } from './types';
import { promiseState } from '@passionware/platform-react';
import { AsyncButton } from 'v5/patterns/forms/fields/AsyncButton';
import { WithCallsheetService } from 'v5/services/io/CallsheetService/CallsheetService';
import { WithProductionService } from 'v5/services/io/ProductionService/ProductionService';
import { WithServices } from 'v5/platform/ts/services';
import { WithAppScopeService } from 'v5/services/internal/AppScopeService/AppScopeService';
import { WithEnterpriseService } from 'v5/services/io/EnterpriseService/EnterpriseService';
import { WithFeatureService } from 'v5/services/front/FeatureService/FeatureService';
import { ScopeBadge } from 'v5/patterns/page-layout/ScopeBadge';
import { maybe } from '@passionware/monads';
import { workspaceScopingQueryUtils } from 'v5/api/_common/query/workspace-scoping';
import { Callsheet, Production } from '__types__';
import { dateConvert } from 'v5/platform/lang/dateConvert';
import { rd } from '@passionware/monads';
import { CallsheetDetailsFields } from './_shared/CallsheetDetailsFields';
import { ProductionResourcesPreview } from './_shared/ProductionResourcesPreview';
import { useProductionResourceCounts } from './_shared/useProductionResourceCounts';
import { useResponsiveDialogBody } from 'v5/design-system/useResponsiveDialogBody';
import { cn } from 'v5/platform/dom/cn';

export interface DuplicateCallsheetDialogContentProps
  extends WithServices<
    [
      WithCallsheetService,
      WithProductionService,
      WithAppScopeService,
      WithEnterpriseService,
      WithFeatureService
    ]
  > {
  /** The original callsheet to duplicate */
  originalCallsheet: Callsheet;
  onSubmit?: (data: DuplicateCallsheetFormData) => Promise<void> | void;
  open?: boolean;
}

/**
 * Duplicate Callsheet Dialog Content - v5 implementation
 *
 * Allows duplicating a callsheet with a new date.
 * For callsheets linked to productions, shows confirmed resources for the selected date.
 */
export function DuplicateCallsheetDialogContent({
  onSubmit,
  open = true,
  services,
  originalCallsheet
}: DuplicateCallsheetDialogContentProps) {
  const mutation = promiseState.useMutation(
    async (data: DuplicateCallsheetFormData) => {
      return onSubmit?.(data);
    }
  );

  // Responsive scrolling for dialog body
  const responsive = useResponsiveDialogBody(600);

  // Get workspace ID for fetching productions
  const app_id = useCurrentTeamName();

  // Get linked production IDs from callsheet
  const linkedProductionIds = useMemo(() => {
    return (
      originalCallsheet.linked_productions?.map(lp => lp.id) ??
      (originalCallsheet.production_id ? [originalCallsheet.production_id] : [])
    );
  }, [originalCallsheet]);

  // Fetch productions with full data (including bookings) using productionService
  // This is needed to calculate resource counts for the selected date
  const productionsRecord = services.productionService.useProductionsById(
    app_id ?? '',
    linkedProductionIds
  );

  // Extract productions from record
  const selectedProductions = useMemo(() => {
    return linkedProductionIds
      .map(id => {
        const productionData = productionsRecord[id];
        return rd.tryGet(productionData);
      })
      .filter((p): p is Production => p !== undefined);
  }, [productionsRecord, linkedProductionIds]);

  // Parse original from_date to CalendarDate
  const defaultFromDate = useMemo(() => {
    if (originalCallsheet.from_date) {
      // Use dateConvert.calendarDate.from() which handles both Date and string
      const calendarDate = dateConvert.calendarDate.from(
        originalCallsheet.from_date
      );
      if (calendarDate) {
        return calendarDate;
      }
    }
    return today(getLocalTimeZone());
  }, [originalCallsheet.from_date]);

  // Parse original call_time
  const defaultCallTime = useMemo(() => {
    if (originalCallsheet.call_time) {
      const timeStr =
        typeof originalCallsheet.call_time === 'string'
          ? originalCallsheet.call_time
          : '';
      const match = timeStr.match(/(\d{1,2}):(\d{2})/);
      if (match) {
        return new Time(parseInt(match[1], 10), parseInt(match[2], 10));
      }
    }
    return new Time(9, 0);
  }, [originalCallsheet.call_time]);

  const form = useForm<DuplicateCallsheetFormData>({
    defaultValues: {
      originalCallsheetId: originalCallsheet.id,
      fromDate: defaultFromDate,
      title: originalCallsheet.title ?? '',
      dateSelection: { type: 'single', date: defaultFromDate },
      generalCallTime: defaultCallTime
    }
  });

  const dateSelection = form.watch('dateSelection');
  const generalCallTime = form.watch('generalCallTime');

  // Auto-focus the title field when the dialog opens
  useEffect(() => {
    requestAnimationFrame(() => {
      form.setFocus('title');
    });
  }, [form]);

  // Register dirty state with UnsavedChangesProtection context
  useRegisterDirtyState(form.formState.isDirty);

  // Get resource counts using shared hook
  const resourceCount = useProductionResourceCounts({
    productionsRecord,
    dateSelection,
    generalCallTime
  });

  const handleSubmit = (data: DuplicateCallsheetFormData) => {
    // Update fromDate based on dateSelection for the mutation
    const submitData: DuplicateCallsheetFormData = {
      ...data,
      fromDate:
        data.dateSelection.type === 'single'
          ? data.dateSelection.date
          : data.dateSelection.from
    };
    mutation.track(submitData);
  };

  return (
    <Form {...form}>
      <FormDialogContent
        onSubmit={form.handleSubmit(handleSubmit)}
        open={open}
        title={
          <>
            Duplicate callsheet
            {/* The copy is created where the original lives, not in the browsing scope. */}
            <ScopeBadge
              scope={maybe.map(
                workspaceScopingQueryUtils.ofEntity(originalCallsheet),
                workspace => ({
                  ...workspace,
                  teamId: originalCallsheet.enterprise_team_id ?? null
                })
              )}
              meaning="createsIn"
              services={services}
            />
          </>
        }
        saveText="Duplicate"
        mutation={mutation.state}
        description="Create a copy of this callsheet with a new date. Resources from linked productions will be updated based on confirmed bookings for the selected date."
        className="max-w-4xl h-[70vh] min-h-[calc(min(700px,100vh-2rem))]"
        style={responsive.containerStyle}
        renderDialogBody={children => (
          <DialogBody
            className={cn('space-y-6', responsive.bodyClassName)}
            scrollerClassName={responsive.scrollerClassName}
          >
            {children}
          </DialogBody>
        )}
        footerContent={<DialogErrorFooter mutation={mutation.state} />}
        saveButtonSlot={
          <AsyncButton
            type="submit"
            mutation={mutation.state}
            variant="primary"
          >
            Duplicate
          </AsyncButton>
        }
      >
        {/* Form Fields */}
        <CallsheetDetailsFields form={form} defaultDate={defaultFromDate} />

        {/* Resources Section */}
        <ProductionResourcesPreview
          productions={selectedProductions}
          resourceCount={resourceCount}
          title="Resources from productions"
          description="Resources booked on the selected date will be added into the callsheet."
          emptyMessage="This callsheet is not linked to any productions. The existing resources will be copied as-is."
        />
      </FormDialogContent>
    </Form>
  );
}
